import Modal from '../Modal/Modal';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import AddLocationForm from '../AddLocationForm/AddLocationForm';
import { getReverseGeocode, searchForLocations } from '../../api/locations';
import styles from './AddLocationModal.module.less';
import Map from '../Map';
import { Button } from '../Button/Button';
import { isMobile, isTablet } from '../../utils/browser';
import {
  reverseGeocodingBlocked,
  whenLocationIsCreated,
  whenLocationIsFailed
} from '../../screens/deliveries/messages';
import { connect } from 'react-redux';
import { CUSTOM_LOCATION_ID } from '../../utils/constants';
import { getErrorFromResponse } from '../../utils/apiHelpers';
import {
  addNewAddressScreenOpen,
  cancelAddNewAddressClicked,
  saveThisAddressClicked
} from '../../utils/analytika/events';
import { MapUseCase } from '../Map/Map';
import publishAV2Event from '../../utils/analytika/reporterAnalytikaV2';
import { ANALYTIKA_EVENTS_V2 } from '../../utils/analytika/enums';
import { FeatureFlag } from '../../utils/galileo/featureFlag.enum';

export class AddLocationModal extends Component {
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.formRef = React.createRef();
  }

  formRef;

  initialState = {
    availableLocations: [],
    locationSearchLoading: false,
    cityId: undefined,
    location: undefined,
    isSubmitting: false
  };
  state = this.initialState;

  componentDidMount() {
    const { publishAnalytikaEvent, visible } = this.props;
    visible && publishAnalytikaEvent(addNewAddressScreenOpen());
  }

  onLocationSearch = (cityId, query) => {
    if (!cityId || !query) {
      return;
    }
    const selectedCity = this.props.cities.find(city => city.id === cityId);

    this.setState({
      locationSearchLoading: true
    });

    searchForLocations(query, {
      lat: selectedCity.lat,
      lng: selectedCity.lng
    })
      .then(availableLocations => {
        this.setState({
          availableLocations,
          locationSearchLoading: false
        });
      })
      .catch(() => {
        this.setState({
          locationSearchLoading: false
        });
      });
  };
  getLocationById = locationId => {
    return this.state.availableLocations?.find(location => location.id === locationId);
  };
  onLocationChange = locationId => {
    const location = this.getLocationById(locationId);
    this.setState({
      location
    });
  };
  onMapLocationChange = location => {
    const { t } = this.props;

    // Store original state to potentially revert back to
    const originalLocation = this.state.location;

    this.setState({
      locationSearchLoading: true,
      location: {
        location,
        formattedAddress: t('modal.addNewLocation.location.loadingText'),
        id: CUSTOM_LOCATION_ID,
        customLocation: true
      }
    });

    // Block reverse geocoding if the feature flag is disabled
    if (this.props.isReverseGeocodingDisabled) {
      // eslint-disable-next-line no-console
      console.error('Reverse geocoding is blocked for the user');
      reverseGeocodingBlocked();
      // Revert to original state instead of keeping provisional pin
      this.setState({
        locationSearchLoading: false,
        location: originalLocation // Restore the original location
      });
      return;
    }

    getReverseGeocode(location)
      .then(nearbyLocation => {
        this.setState({
          locationSearchLoading: false,
          location: {
            ...nearbyLocation,
            customLocation: true
          }
        });
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error loading location details', error);
        this.setState({
          locationSearchLoading: false,
          location: undefined
        });
      });
  };

  _postLocationCreation = error => {
    if (!error) {
      this.formRef.form?.current.resetFields();
      whenLocationIsCreated();
    } else {
      whenLocationIsFailed(getErrorFromResponse(error));
    }
  };
  onCityChange = cityId => {
    this.setState({
      cityId
    });
  };

  async onSubmit() {
    const form = this.formRef?.current;
    let values;
    const { createAddress, cities, publishAnalytikaEvent } = this.props;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const locationData = this.state.location;
    const { city } = values;
    const { name: cityName } = cities.find(cityItem => cityItem.id === city);
    const requestData = {
      nickname: values.nickname,
      lat: locationData.location.lat,
      lng: locationData.location.lng,
      street: locationData.street,
      building: locationData.building,
      area: locationData.area,
      number: values.buildingNumber,
      note: values.note,
      setAsDefault: values.setAsDefault,
      city: cityName
    };
    this.setState({
      isSubmitting: true
    });
    publishAnalytikaEvent(
      saveThisAddressClicked({
        city: cityName,
        address_nickname: values.nickname,
        street: locationData.street,
        building: locationData.building,
        lat: locationData.location.lat,
        lng: locationData.location.lng,
        additional_details: values.note,
        default_address_checked: Boolean(values.setAsDefault)
      })
    );
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_SAVE_ADDRESS,
      event_version: 2,
      picked_full_address: `${locationData.nickname}, ${location?.street}, ${location?.building}`
    });

    try {
      const response = await createAddress(requestData);
      this._postLocationCreation();
      await this.onClose(response?.id);
    } catch (err) {
      this._postLocationCreation(err);
    } finally {
      this.setState({
        isSubmitting: false
      });
    }
  }

  onClose = (...params) => {
    this.formRef?.props?.form?.resetFields?.();
    this.setState(this.initialState);
    this.props.onClose?.(...params);
  };

  onModalClose = () => {
    const { publishAnalytikaEvent } = this.props;
    const { location } = this.state;
    publishAnalytikaEvent(cancelAddNewAddressClicked());
    publishAV2Event({
      event_name: ANALYTIKA_EVENTS_V2.EXPR_CANCEL_ADDRESS,
      event_version: 2,
      picked_full_address:
        location?.formattedAddress || location?.name || `${location?.city}, ${location?.area}, ${location?.building}`
    });
    this.onClose();
  };

  render() {
    const { visible, cities, t, defaultPickupLocation, existingAddresses = [] } = this.props;
    const { availableLocations, locationSearchLoading, location, cityId, isSubmitting } = this.state;
    let selectedLocation = location;
    let locations = availableLocations;
    const defaultCity =
      (cities.length &&
        cities.find(city => {
          return city.id === (cityId || defaultPickupLocation?.city_id) || city.name === defaultPickupLocation?.city;
        })) ||
      cities[0];
    if (!selectedLocation && defaultCity) {
      selectedLocation = {
        location: {
          ...defaultCity
        }
      };
    }
    if (location?.customLocation) {
      locations = [location, ...availableLocations];
    }
    return (
      <Modal
        width={800}
        className={styles.modal}
        titleText={t('modal.addNewLocation.title')}
        visible={visible}
        onClose={this.onModalClose}
        footer={
          <Row type="flex" justify="end" gutter={[24]}>
            <Col xs={12} lg={7}>
              <Button size={'large'} block onClick={this.onModalClose}>
                {t('general.cancel')}
              </Button>
            </Col>
            <Col xs={12} lg={7}>
              <Button
                type="primary"
                size={'large'}
                block
                disabled={isSubmitting || locationSearchLoading}
                onClick={this.onSubmit}
                data-testid="submitButton"
              >
                {t('modal.addNewLocation.saveLocationButton')}
              </Button>
            </Col>
          </Row>
        }
      >
        <Row type="flex">
          <Col xs={24} lg={12} order={isMobile() || isTablet() ? 2 : 1}>
            <AddLocationForm
              formRef={this.formRef}
              onLocationSearch={this.onLocationSearch}
              onLocationChange={this.onLocationChange}
              existingAddresses={existingAddresses}
              availableCities={cities}
              availableLocations={locations}
              defaultCityId={defaultCity?.id}
              location={location}
              locationSearchLoading={locationSearchLoading}
              onCityChange={this.onCityChange}
            />
          </Col>
          <Col xs={24} lg={12} order={isMobile() || isTablet() ? 1 : 2}>
            <div className={styles.map}>
              {selectedLocation && (
                <Map
                  mapUseCase={MapUseCase.CreatePickup}
                  showMarkerInfo={false}
                  dropOff={selectedLocation}
                  showDirections={false}
                  onDropOffMarkerChange={this.onMapLocationChange}
                  options={{
                    zoomControl: undefined //default behavior (show for big maps, hide on small <200px)
                  }}
                />
              )}
            </div>
          </Col>
        </Row>
      </Modal>
    );
  }
}

AddLocationModal.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  createAddress: PropTypes.func,
  cities: PropTypes.array.isRequired,
  t: PropTypes.any.isRequired,
  defaultPickupLocation: PropTypes.object,
  existingAddresses: PropTypes.array,
  publishAnalytikaEvent: PropTypes.func,
  isReverseGeocodingDisabled: PropTypes.bool
};

const mapState = ({ user: { cities, defaultPickupLocation, userGalileoVars } }) => ({
  cities,
  defaultPickupLocation,
  isReverseGeocodingDisabled: userGalileoVars?.[FeatureFlag.IS_LOCATIONS_REVERSE_GEOCODING_DISABLED]
});

const mapDispatch = ({ user: { createAddress, publishAnalytikaEvent } }) => ({
  createAddress,
  publishAnalytikaEvent
});

export default connect(mapState, mapDispatch)(withTranslation()(AddLocationModal));
